/**
 * Country display names for the ISO-3166 alpha-2 codes stored on the CDT Benefit contract (rule 43).
 * The contract and the API keep the two-letter code; the browser's built-in Intl.DisplayNames supplies the
 * English name ("India", "United States") so no country table has to be shipped or maintained. Codes the
 * browser does not know (or environments without Intl.DisplayNames) fall back to the code itself.
 */
const SPECIAL: Record<string, string> = { EU: 'European Union' };
const cache = new Map<string, string>();
let displayNames: Intl.DisplayNames | null | undefined;

function resolver(): Intl.DisplayNames | null {
  if (displayNames !== undefined) return displayNames;
  try {
    displayNames = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;
  } catch {
    displayNames = null;
  }
  return displayNames;
}

/** "IN" -> "India"; unknown or empty codes come back unchanged (upper-cased). */
export function countryName(code: string | null | undefined): string {
  const upper = (code ?? '').trim().toUpperCase();
  if (!upper) return '';
  if (SPECIAL[upper]) return SPECIAL[upper];
  const cached = cache.get(upper);
  if (cached) return cached;
  let name = upper;
  try {
    const resolved = resolver()?.of(upper);
    // Intl returns 'Unknown Region' for codes it does not know (e.g. the ZZ test country): keep the code instead.
    if (resolved && resolved !== upper && !/unknown/i.test(resolved)) name = resolved;
  } catch {
    /* invalid region code for Intl - keep the code */
  }
  cache.set(upper, name);
  return name;
}

/** "India (IN)" or, with a currency, "India (IN) · INR". The code stays visible because it is what the chain stores. */
export function countryLabel(code: string | null | undefined, currency?: string | null): string {
  const upper = (code ?? '').trim().toUpperCase();
  if (!upper) return '';
  const name = countryName(upper);
  const base = name === upper ? upper : `${name} (${upper})`;
  const cur = (currency ?? '').trim().toUpperCase();
  return cur ? `${base} · ${cur}` : base;
}

/** Common city / informal names members type instead of the country name (code -> aliases). */
export const COUNTRY_ALIASES: Record<string, string[]> = {
  AE: ['UAE', 'Dubai', 'Abu Dhabi', 'Sharjah', 'Emirates'],
  US: ['USA', 'America', 'United States of America', 'New York'],
  GB: ['UK', 'England', 'Britain', 'London', 'Scotland', 'Wales'],
  IN: ['Bharat', 'Hindustan', 'Delhi', 'Mumbai'],
  SA: ['KSA', 'Saudi', 'Riyadh', 'Jeddah'],
  NL: ['Holland', 'Amsterdam'],
  CZ: ['Czech Republic', 'Prague'],
  KR: ['Korea', 'Seoul'],
  RU: ['Russian Federation', 'Moscow'],
  VN: ['Viet Nam', 'Hanoi', 'Saigon'],
  HK: ['Hongkong'],
  TR: ['Turkiye', 'Istanbul'],
  MM: ['Burma'],
  CI: ['Cote d Ivoire', 'Ivory Coast'],
  CD: ['DRC', 'Congo Kinshasa'],
  CG: ['Congo Brazzaville'],
  LK: ['Ceylon', 'Colombo'],
  BD: ['Dhaka'],
  PK: ['Karachi', 'Lahore', 'Islamabad'],
  NP: ['Kathmandu'],
  QA: ['Doha'],
  KW: ['Kuwait City'],
  OM: ['Muscat'],
  BH: ['Manama'],
  SG: ['Singapura'],
  MY: ['Kuala Lumpur'],
  TH: ['Bangkok'],
  PH: ['Manila'],
  ID: ['Jakarta', 'Bali'],
  AU: ['Sydney', 'Melbourne'],
  CA: ['Toronto', 'Vancouver'],
  DE: ['Deutschland', 'Berlin'],
  FR: ['Paris'],
  IT: ['Italia', 'Rome'],
  ES: ['Espana', 'Madrid'],
  CH: ['Swiss', 'Zurich'],
  ZA: ['RSA', 'Johannesburg'],
  EG: ['Cairo'],
  NG: ['Lagos'],
  KE: ['Nairobi'],
  BR: ['Brasil', 'Sao Paulo'],
  MX: ['Mexico City'],
  JP: ['Nippon', 'Tokyo'],
  CN: ['PRC', 'Beijing', 'Shanghai'],
  TW: ['Taipei'],
  MO: ['Macau'],
  EU: ['Euro', 'Eurozone'],
};

/** Lower-case haystack for a country picker search: name, code, currency and aliases ("united arab emirates ae aed uae dubai ..."). */
export function countrySearchText(code: string | null | undefined, currency?: string | null): string {
  const upper = (code ?? '').trim().toUpperCase();
  if (!upper) return '';
  return [countryName(upper), upper, (currency ?? '').toUpperCase(), ...(COUNTRY_ALIASES[upper] ?? [])].join(' ').toLowerCase();
}

/** True when every whitespace-separated term of `query` occurs in the country's search text. */
export function countryMatches(code: string | null | undefined, currency: string | null | undefined, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = countrySearchText(code, currency);
  return terms.every((term) => hay.includes(term));
}

/** Sort helper: alphabetical by display name, then code. */
export function compareCountries(a: string | null | undefined, b: string | null | undefined): number {
  const an = countryName(a); const bn = countryName(b);
  return an.localeCompare(bn, 'en') || (a ?? '').localeCompare(b ?? '');
}
